import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Stack,
  Divider,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PartInfo } from '../types';
import { getPart, getPartVariants } from '../services/api';
import { PartImageCarousel } from '../components/PartImageCarousel';

export const PartDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  const [part, setPart] = useState<PartInfo | null>(location.state?.part || null);
  const [variants, setVariants] = useState<PartInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || variants.length > 0) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let partDetails = part;
        if (!partDetails) {
          partDetails = await getPart(id);
          setPart(partDetails);
        }

        const allVariants = await getPartVariants(id);
        setVariants(allVariants);

        if (partDetails && !allVariants.some(v => v.imageId === partDetails!.imageId)) {
          setVariants([partDetails, ...allVariants]);
        }
      } catch (err) {
        setError('Failed to load part details');
        console.error('Error fetching part data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, part, variants]);

  const handleVariantChange = (selectedVariant: PartInfo) => {
    setPart(selectedVariant);
  };

  const handleBack = () => {
    const returnTo = location.state?.returnTo;
    navigate(returnTo || '/');
  };

  const getPriorityColor = (level: string) => {
    switch (level) {
      case 'Premium': return 'primary';
      case 'Standard': return 'secondary';
      case 'Economy': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 4, sm: 8 } }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 0 } }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button onClick={handleBack} size="small">
            Back to Catalog
          </Button>
        </Box>
      </Container>
    );
  }

  if (!part) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 0 } }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Part not found
          </Alert>
          <Button onClick={handleBack} size="small">
            Back to Catalog
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 2, sm: 4 } }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: { xs: 2, sm: 3 } }}
          variant="outlined"
          size="small"
        >
          Back to Catalog
        </Button>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 2, sm: 4 }
        }}>
          {/* Image Carousel Section */}
          <Box>
            <PartImageCarousel
              variants={variants.length > 0 ? variants : [part]}
              onVariantChange={handleVariantChange}
            />
          </Box>

          {/* Main Information Section */}
          <Box>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {part.name}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  mb: 3,
                  flexWrap: 'wrap',
                  '& .MuiChip-root': {
                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                    height: { xs: 28, sm: 32 }
                  }
                }}
              >
                <Chip
                  label={part.productCategory}
                  variant="outlined"
                />
                <Chip
                  label={part.productSubCategory}
                  variant="outlined"
                />
                <Chip
                  label={part.viparProductLevel}
                  color={getPriorityColor(part.viparProductLevel || 'default')}
                />
              </Stack>

              <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, bgcolor: 'grey.50' }}>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: { xs: 1.5, sm: 2 }
                }}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Supplier Code
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {part.supplierCode}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Supplier Name
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {part.supplierName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Image ID
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {part.imageId}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      Type
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {part.primaryOrOther}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  paragraph
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {part.descriptionProdDescriptionLong}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 3, sm: 4 } }} />

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 2, sm: 4 }
        }}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                gutterBottom
                color="primary"
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                Hierarchy Information
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Category Code
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {part.hierarchyCategoryCode}
                </Typography>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Category Name
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {part.hierarchyCategoryName}
                </Typography>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Sub-Category Code
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {part.hierarchySubCategoryCode}
                </Typography>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Sub-Category Name
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {part.hierarchySubCategoryName}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                gutterBottom
                color="primary"
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                Product Descriptions
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Invoice Description
                </Typography>
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {part.descriptionProdDescriptionInvoice}
                </Typography>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  Short Description
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {part.descriptionProdDescriptionShort}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
};
